import { type RefObject, useEffect, useRef, useState } from 'react';
import { TRANSFORM_CURSORS } from '../core/move';
import { panBy } from '../core/view';
import { type RenderEngine, setEngine } from '../engine';
import { loadEngine } from '../engine/load';
import { useEditorStore } from '../state/editorStore';
import { AdjustPreview } from './AdjustPreview';
import { useMoveTool } from './hooks/useMoveTool';
import { usePainting } from './hooks/usePainting';
import { useShapeTools } from './hooks/useShapeTools';
import { useTextTool } from './hooks/useTextTool';
import { useZoomTool } from './hooks/useZoomTool';
import { Rulers } from './Rulers';
import { SelectionLayer } from './SelectionLayer';
import { TextEditorOverlay } from './TextEditorOverlay';
import { TransformOverlay } from './TransformOverlay';

/**
 * The drawing area: a React shell that owns interaction policy (wheel zoom,
 * pan, grid overlay) and delegates all pixels to the engine mounted inside it.
 *
 * The engine gets its own dedicated child element so React never has to reason
 * about DOM it does not own.
 */
export function CanvasStage({ stageRef }: { stageRef?: RefObject<HTMLDivElement | null> }) {
  const ownRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const previewHostRef = useRef<HTMLDivElement | null>(null);
  const stage = stageRef ?? ownRef;
  const engineRef = useRef<RenderEngine | null>(null);
  const panRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [altHeld, setAltHeld] = useState(false);
  const [panning, setPanning] = useState(false);

  const doc = useEditorStore((s) => s.doc);
  const viewport = useEditorStore((s) => s.viewport);
  const showGrid = useEditorStore((s) => s.showGrid);
  const activeTool = useEditorStore((s) => s.activeTool);
  const moveHover = useEditorStore((s) => s.moveHover);

  /*
   * Mount the renderer once its chunk is ready.
   *
   * Fabric is the largest thing we ship and the chrome does not need it to paint,
   * so it is a separate chunk fetched alongside the app (see `engine/load.ts`,
   * which `main.tsx` kicks off at boot). Everything above the canvas is already
   * interactive while it arrives.
   */
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;

    void loadEngine()
      .then(({ createFabricEngine }) => {
        if (disposed) return;
        const engine = createFabricEngine();
        engineRef.current = engine;
        setEngine(engine);

        const state = useEditorStore.getState();
        state.setEngineId(engine.id);
        engine.mount(host, state.doc, state.viewport, {
          onViewportResize: (size) => {
            const store = useEditorStore.getState();
            const first = store.hostSize.width <= 1 || store.hostSize.height <= 1;
            store.setHostSize(size);
            // First real measurement: frame the document instead of showing a corner.
            if (first) store.zoomToFit();
          },
          onPointerMove: (point) => useEditorStore.getState().setPointer(point),
        });

        const size = engine.getHostSize();
        state.setHostSize(size);
        if (size.width > 1) state.zoomToFit();
      })
      .catch(() => {
        useEditorStore
          .getState()
          .notify('The canvas renderer failed to load. Reload the page to try again.', 'error');
      });

    return () => {
      disposed = true;
      engineRef.current?.unmount();
      setEngine(null);
      useEditorStore.getState().setEngineId(null);
      engineRef.current = null;
    };
  }, []);

  /* Push model and viewport changes into the engine. */
  useEffect(() => {
    engineRef.current?.setDocument(doc);
  }, [doc]);

  useEffect(() => {
    engineRef.current?.setViewport(viewport);
  }, [viewport]);

  /*
   * Native listeners, not React props:
   *  - `wheel` must be non-passive to call preventDefault (React registers it passive).
   *  - capture phase runs before Fabric's own handlers, so panning works even
   *    while the engine is consuming pointer events.
   */
  useEffect(() => {
    const element = stage.current;
    if (!element) return;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const store = useEditorStore.getState();
      const rect = element.getBoundingClientRect();
      const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const zoomGesture = event.ctrlKey || event.metaKey || event.altKey;
      if (zoomGesture) {
        store.zoomBy(Math.exp(-event.deltaY * 0.0016), anchor);
        return;
      }
      const dx = event.shiftKey ? event.deltaY : event.deltaX;
      const dy = event.shiftKey ? event.deltaX : event.deltaY;
      store.setViewport(panBy(store.viewport, { x: -dx, y: -dy }));
    };

    const wantsPan = (event: PointerEvent) =>
      event.button === 1 ||
      (event.button === 0 && (spaceHeld || (event.target as HTMLElement).dataset.pan === ''));

    const onPointerDown = (event: PointerEvent) => {
      if (!wantsPan(event)) return;
      panRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      setPanning(true);
      event.preventDefault();
    };

    const onPointerMove = (event: PointerEvent) => {
      const pan = panRef.current;
      if (!pan || pan.pointerId !== event.pointerId) return;
      const store = useEditorStore.getState();
      store.setViewport(
        panBy(store.viewport, { x: event.clientX - pan.x, y: event.clientY - pan.y }),
      );
      pan.x = event.clientX;
      pan.y = event.clientY;
    };

    const onPointerUp = (event: PointerEvent) => {
      if (panRef.current?.pointerId !== event.pointerId) return;
      panRef.current = null;
      setPanning(false);
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [spaceHeld, stage]);

  /* Space temporarily turns the pointer into a hand, as in every image editor. */
  useEffect(() => {
    const isTyping = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space' && !isTyping(event.target)) setSpaceHeld(true);
      if (event.key === 'Alt') setAltHeld(true);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') setSpaceHeld(false);
      if (event.key === 'Alt') setAltHeld(false);
    };
    const onBlur = () => {
      setSpaceHeld(false);
      setAltHeld(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  usePainting(stage, previewHostRef);
  useShapeTools(stage, previewHostRef);
  useTextTool(stage);
  useMoveTool(stage);
  useZoomTool(stage);

  const docRect = {
    left: viewport.pan.x,
    top: viewport.pan.y,
    width: doc.width * viewport.zoom,
    height: doc.height * viewport.zoom,
  };

  const cursor = panning
    ? 'grabbing'
    : spaceHeld
      ? 'grab'
      : activeTool === 'zoom'
        ? altHeld
          ? 'zoom-out'
          : 'zoom-in'
        : activeTool === 'move' && moveHover
          ? (TRANSFORM_CURSORS[moveHover] ?? 'default')
          : 'default';

  return (
    <div
      ref={stage}
      data-testid="stage"
      className="relative min-w-0 flex-1 overflow-hidden bg-app"
      style={{ cursor }}
    >
      {/* Transparency checkerboard, sized to the document, behind the canvas. */}
      <div className="wp-checkerboard absolute" style={docRect} aria-hidden />
      <div ref={hostRef} className="absolute inset-0" />
      {showGrid ? (
        <div
          className="wp-grid absolute"
          style={{
            ...docRect,
            backgroundSize: `${gridStep(viewport.zoom) * viewport.zoom}px ${
              gridStep(viewport.zoom) * viewport.zoom
            }px`,
          }}
          aria-hidden
        />
      ) : null}
      <SelectionLayer stageRef={stage} />
      <TransformOverlay />
      {/* Live brush preview lives here so a stroke can be shown without
          touching the document until it is finished. */}
      <div ref={previewHostRef} className="pointer-events-none absolute inset-0 overflow-hidden" />
      <TextEditorOverlay stageRef={stage} />
      <AdjustPreview />
      <Rulers stageRef={stage} />
    </div>
  );
}

/** Document-space grid spacing that keeps on-screen lines readable at any zoom. */
export function gridStep(zoom: number): number {
  if (zoom >= 2) return 8;
  if (zoom >= 1) return 16;
  if (zoom >= 0.5) return 32;
  if (zoom >= 0.25) return 64;
  if (zoom >= 0.1) return 128;
  return 256;
}
